/**
 * Permission Store (Zustand)
 *
 * Single source of truth for "what can the signed-in user do":
 * - Loaded once at login (see authSlice) from GET /api/rbac/my-permissions and
 *   kept for the session; reset on logout.
 * - Auto-refetches when any API call returns 403 with result.code=RBAC_DENIED
 *   (api/axios.ts dispatches the 'rbac:denied' window event) — permissions
 *   changed mid-session, so the UI re-gates itself.
 *
 * Consumers:
 *   <Can perm="userMgmt.role.view">…</Can>       — components/common/Can
 *   const allowed = useCan('userMgmt.role.view'); — hooks/usePermissions
 *   usePermissionStore.getState().can(key)         — outside React
 *
 * FAIL-OPEN RULE (deliberate): until `loaded` is true (or if the fetch fails),
 * gating helpers return true so the portal never blanks out because of a
 * slow/failed permissions call — the backend interceptor is the real
 * enforcement; UI gating is convenience.
 */

import { create } from 'zustand';

import { rbacService } from '@/api/services/rbac.service';

interface PermissionState {
  /** Effective permission keys held by the current user. */
  permissions: Set<string>;
  /** Owner holds everything and bypasses all gates. */
  isOwner: boolean;
  /** userType echoed by /my-permissions (for reference / debugging). */
  userType: string | null;
  /** Whether /my-permissions has resolved at least once. */
  loaded: boolean;
  /** Whether a load is currently in flight. */
  loading: boolean;
}

interface PermissionActions {
  /** Loads once; pass { force: true } to refetch. */
  load: (opts?: { force?: boolean }) => Promise<void>;
  /**
   * True when the user holds the permission. Accepts a single key or an array
   * (ANY-of). Owners hold everything. Fails OPEN until loaded.
   */
  can: (perm: string | string[]) => boolean;
  /** ALL-of variant for compound actions. */
  canAll: (perms: string | string[]) => boolean;
  /** Clear on logout. */
  reset: () => void;
}

type PermissionStore = PermissionState & PermissionActions;

const initialState: PermissionState = {
  permissions: new Set<string>(),
  isOwner: false,
  userType: null,
  loaded: false,
  loading: false,
};

export const usePermissionStore = create<PermissionStore>((set, get) => ({
  ...initialState,

  load: async ({ force = false } = {}) => {
    const { loaded, loading } = get();
    if (loading || (loaded && !force)) return;
    set({ loading: true });
    try {
      const response = await rbacService.getMyPermissions();
      if (response?.statusCode === 200 && response.result) {
        set({
          permissions: new Set(response.result.permissions ?? []),
          isOwner: !!response.result.isOwner,
          userType: response.result.userType ?? null,
          loaded: true,
        });
      }
    } catch (error) {
      // Fail open: leave loaded=false; gates render permissive.
      console.error('Failed to load permissions:', error);
    } finally {
      set({ loading: false });
    }
  },

  can: (perm) => {
    const { isOwner, permissions, loaded } = get();
    if (!loaded) return true; // fail-open before/without load
    if (isOwner) return true;
    if (Array.isArray(perm)) return perm.some((p) => permissions.has(p));
    return permissions.has(perm);
  },

  canAll: (perms) => {
    const { isOwner, permissions, loaded } = get();
    if (!loaded) return true;
    if (isOwner) return true;
    return (Array.isArray(perms) ? perms : [perms]).every((p) => permissions.has(p));
  },

  reset: () => set({ ...initialState, permissions: new Set<string>() }),
}));

// Permissions changed mid-session (backend answered RBAC_DENIED) → refetch and
// let every gate re-evaluate. Event dispatched by api/axios.ts.
if (typeof window !== 'undefined') {
  window.addEventListener('rbac:denied', () => {
    void usePermissionStore.getState().load({ force: true });
  });
}
